import { ReactNode, useMemo } from 'react';
import MiniLoading from '../../../core/MiniLoading';

import {
  StyledEmptyKpiCard, StyledLoadContainer, ButtonsContainer, ButtonsColumn,
} from './styled/ErrorKpi.styled';

import Button from '../../../core/Button';

const messagesMap: Record<string, string> = {
  EmptyDataSet: `O seu KPI foi criado e as informações nos dados estão vazias, portanto não podemos renderizar o seu gráfico.
  Verifique as colunas e filtros fornecidos na criação de seu gráfico!`,
  TooManyCategories: `O seu KPI possui mais de 100 categorias, o que está impactando no consumo de memória do seu navegador, portanto não podemos renderizar o seu gráfico.
  Recomendamos utilizar agrupamentos e filtros para otimização da leitura! (Recomendado 20 categorias)`,
  HasNegativeValues: `O seu KPI possui valores negativos que não são aceitos por esse formato de gráfico, portanto não podemos renderizá-lo.
  Recomendamos a utilização de outros formatos de gráficos para esses dados!`,
  NoLineSelected: `Este formato de gráfico precisa ter uma variável selecionada como Linha para ser renderizado.
  Recomendamos a utilização de outros formatos de gráficos para esses dados!`,
  NeedsDateFormat: `Este formato de gráfico precisa de uma variável de DATA selecionada como Linha, no formato A-M-D.
  Gráfico de calendário não será renderizado!`,
  NeedsNumericValue: `Este formato de gráfico precisa de uma variável numérica selecionada como Valor.
  Diagrama de caixa não será renderizado!`,
  NeedsTwoNumericValue: `Este formato de gráfico precisa de duas variável numérica selecionadas como Valores.
  Portanto não será renderizado!`,
  InvalidInStory: `Este indicador só consegue acessar os dados mais recentes.
  Portanto, não será renderizada em um story.`,
  AIEmptyDataSet: `Os dados informados ao Smart Insight estão vazios.
  Verifique as colunas e filtros ou tente refazer sua pergunta.`,
  AICodeError: `O Smart Insight não conseguiu gerar nenhuma resposta.
  Verifique os dados fornecidos ou tente refazer sua pergunta.`,
};

const reasonsMap: Record<string, string> = {
  EmptyDataSet: 'Indicador vazio',
  TooManyCategories: 'KPI com muitas categorias',
  HasNegativeValues: 'KPI com valores negativos',
  NoLineSelected: 'KPI sem linha',
  NeedsDateFormat: 'KPI com valores inválidos',
  NeedsNumericValue: 'KPI com valores inválidos',
  NeedsTwoNumericValue: 'KPI com valores inválidos',
  InvalidInStory: 'Indicador inconsistente',
  AIEmptyDataSet: 'Smart Insight vazio',
  AICodeError: 'Smart Insight inválido',
  UpdateError: 'Erro ao atualizar o KPI',
};

/** Define a limit for the number of categories in the KPI to prevent excessive memory usage */
const LOWKPILIMIT = 101;
const HIGHKPILIMIT = 1001;
const LOWLIMITEDKPIS = [
  'Bar', 'Pie', 'Line', 'Radar', 'Funnel', 'RadialBar', 'Pareto', 'Histogram', 'BoxPlot', 'Bullet', 'Gauge',
];
const NOTLIMITEDKPIS = ['Table', 'Value'];
const NOHISTORYKPIS = ['SubsetTable', 'plotly'];
const NOVALUEMODIFIERS = [
  'label', 'smartLabel', 'BoxPlot', 'ScatterPlot', 'Iframe', 'Histogram', 'Content',
];

export const checkKpiLimit = (kpiType: string, ...lengths: number[]): boolean => {
  if (NOTLIMITEDKPIS.includes(kpiType)) return false;
  const maxLength = Math.max(...lengths);
  return (LOWLIMITEDKPIS.includes(kpiType) && maxLength > LOWKPILIMIT) || maxLength > HIGHKPILIMIT;
};

export const isKpiInvalidInStory = (kpiType: string): boolean => NOHISTORYKPIS.includes(kpiType);

export const allowValueModifiers = (kpiType: string): boolean => (
  !NOVALUEMODIFIERS.includes(kpiType)
);

interface KpiStatusProps {
  isLoading?: boolean,
  conditions?: Array<[string, boolean]>,
  reason?: string,
  message?: string,
  autoValueModifiers?: (type: string, value: string) => void;
  children?: ReactNode,
  drilldownSelected?: boolean,
  removeDrilldown?: () => void,
}

const KpiStatus = ({
  isLoading,
  autoValueModifiers,
  conditions = [],
  reason,
  message,
  children,
  drilldownSelected = false,
  removeDrilldown = () => {},
}: KpiStatusProps) => {
  const error = useMemo(() => (
    conditions.find(([, condition]) => condition)?.[0]
  ), [conditions]);

  const lesserGroup = () => autoValueModifiers && autoValueModifiers('group', 'not-most_inc');
  const greaterGroup = () => autoValueModifiers && autoValueModifiers('group', 'not-least_inc');
  const leastFilter = () => autoValueModifiers && autoValueModifiers('filter', 'least');
  const mostFilter = () => autoValueModifiers && autoValueModifiers('filter', 'most');

  return (
    <>
      {isLoading && (
        <StyledLoadContainer><MiniLoading fill="primary" /></StyledLoadContainer>
      )}
      {error && !isLoading && (
        <>
          {drilldownSelected && (
            <div style={{
              left: '10px',
              position: 'absolute',
              top: '10px',
              zIndex: '1',
            }}
            >
              <Button
                color="secondary"
                variant="outlined"
                onClick={removeDrilldown}
              >
                <span style={{ fontSize: '12px' }}>
                  Voltar
                </span>
              </Button>
            </div>
          )}
          <StyledEmptyKpiCard>
            <h2>{reason ?? reasonsMap[error]}</h2>
            <p>{message ?? messagesMap[error]}</p>
            {error === 'TooManyCategories' && Boolean(autoValueModifiers) && (
              <ButtonsContainer>
                <ButtonsColumn>
                  <Button
                    color="secondary"
                    size="small"
                    textTransform="none"
                    onClick={lesserGroup}
                    style={{ margin: '2px' }}
                  >
                    Agrupar os menores valores
                  </Button>
                  <Button
                    color="secondary"
                    size="small"
                    textTransform="none"
                    onClick={greaterGroup}
                    style={{ margin: '2px' }}
                  >
                    Agrupar os maiores valores
                  </Button>
                </ButtonsColumn>
                <ButtonsColumn>
                  <Button
                    color="secondary"
                    size="small"
                    textTransform="none"
                    onClick={leastFilter}
                    style={{ margin: '2px' }}
                  >
                    Mostrar os 20 menores valores
                  </Button>
                  <Button
                    color="secondary"
                    size="small"
                    textTransform="none"
                    onClick={mostFilter}
                    style={{ margin: '2px' }}
                  >
                    Mostrar os 20 maiores valores
                  </Button>
                </ButtonsColumn>
              </ButtonsContainer>
            )}
          </StyledEmptyKpiCard>
        </>
      )}
      {!error && !isLoading && children}
    </>
  );
};

export default KpiStatus;
